import { useEffect, useState, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { debounce } from 'lodash'
import { Dropdown, SearchElement, type IDropdownProps } from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { InboxesDropdownContent } from 'widgets/SearchDropdown/ui/DropdownContent/InboxesDropdownContent/InboxesDropdownContent'
import { type SearchDropdownInboxesStore } from 'widgets/SearchDropdown/store/searchDropdownInboxesStore'

type TeamElementProps = {
  parentsDropdownId?: string[]
  store: SearchDropdownInboxesStore
  onRemove: (type: SearchTypesEnum) => void
  activeType: SearchTypesEnum | null
  canShowChildDropdown: boolean
  dropdownProps?: Partial<IDropdownProps>
}

export const TeamElement: FC<TeamElementProps> = observer(
  ({ parentsDropdownId, store, onRemove, activeType, canShowChildDropdown, dropdownProps }) => {
    const [show, setShow] = useState(false)
    const { inboxesStore } = useEntitiesContext()
    const { hasSelectedItems, selectedItemsCount, firstSelectedItemId, disableClosing } = store

    const getLabel = () => {
      const firstItem = inboxesStore.getItem(firstSelectedItemId)

      if (hasSelectedItems && selectedItemsCount === 1 && firstItem) {
        return firstItem.name
      }

      return `${selectedItemsCount} inboxes`
    }

    const onShow = () => {
      const showDropdown = activeType === SearchTypesEnum.teams && canShowChildDropdown

      setShow(showDropdown)
    }

    const showDebounce = debounce(onShow, 300)

    useEffect(() => {
      showDebounce()
    }, [canShowChildDropdown, activeType])

    return (
      <Dropdown
        ariaLabel={'TeamElement'}
        parentsDropdownId={parentsDropdownId}
        iconButtonProps={{
          size: 'medium',
          fontSize: 20,
          ariaLabel: 'TeamElement_trigger',
        }}
        show={show}
        triggerComponent={(open) => (
          <SearchElement
            icon={'all_inboxes'}
            type='Inbox'
            removeCallback={() => {
              onRemove(SearchTypesEnum.teams)
            }}
            content={hasSelectedItems && getLabel()}
            warningContent={!open && !hasSelectedItems}
            contentDefaultValue='select'
            isViewOnly={disableClosing}
          />
        )}
        placement={'bottom-start'}
        sizeTriggerButton={'medium'}
        variantTriggerButton={'icon'}
        customComponent={() => <InboxesDropdownContent store={store} />}
        {...dropdownProps}
      />
    )
  }
)
